import { FC } from 'react';

import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';

import { ON_GO_COLORS } from '../onGoColors';
import { groupTrueFalseAnswers } from './groupExerciseAnswers';
import { resolveOnGoChoiceDisplayState } from './onGoChoiceDisplay';

const useStyles = makeStyles(theme => ({
  groups: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  groupRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
  groupIndex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 28,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  groupChoices: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: theme.spacing(1),
  },
  choice: {
    width: '100%',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  selectedChoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  correctChoice: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
  },
  incorrectChoice: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
  },
  mutedChoice: {
    opacity: 0.7,
  },
}));

type OnGoGroupedMultiSelectChoicesProps = {
  answers: ExerciseAnswer[];
  correctAnswerIdSet: Set<number>;
  disabled?: boolean;
  isAttemptCorrect: boolean | null;
  isResultPhase: boolean;
  onSelect?: (answerId: number, group: number) => void;
  onToggle?: (answerId: number) => void;
  selectedAnswerIds: number[];
};

const OnGoGroupedMultiSelectChoices: FC<OnGoGroupedMultiSelectChoicesProps> = ({
  answers,
  correctAnswerIdSet,
  disabled = false,
  isAttemptCorrect,
  isResultPhase,
  onSelect,
  onToggle,
  selectedAnswerIds,
}) => {
  const classes = useStyles();
  const groups = groupTrueFalseAnswers(answers);
  const selectedAnswerIdSet = new Set(selectedAnswerIds);
  const selectedByGroup = new Map<number, number>();

  for (const answerId of selectedAnswerIds) {
    const answer = answers.find(item => item.id === answerId);
    if (answer?.group != null) {
      selectedByGroup.set(answer.group, answerId);
    }
  }

  const getChoiceClassName = (answer: ExerciseAnswer) => {
    const displayState = resolveOnGoChoiceDisplayState(
      isResultPhase,
      selectedAnswerIdSet.has(answer.id),
      correctAnswerIdSet.has(answer.id),
      isAttemptCorrect,
    );

    switch (displayState) {
      case 'question-selected':
        return clsx(classes.choice, classes.selectedChoice);
      case 'question-unselected':
        return classes.choice;
      case 'result-correct':
        return clsx(classes.choice, classes.correctChoice);
      case 'result-incorrect':
        return clsx(classes.choice, classes.incorrectChoice);
      case 'result-muted':
        return clsx(classes.choice, classes.mutedChoice);
    }
  };

  return (
    <Box className={classes.groups}>
      {groups.map(({ group, choices }, index) => (
        <Box className={classes.groupRow} key={group}>
          <Typography className={classes.groupIndex} variant='body2'>
            {index + 1}.
          </Typography>
          <Box className={classes.groupChoices}>
            {choices.map(answer => {
              const isSelected = onSelect ? selectedByGroup.get(group) === answer.id : selectedAnswerIdSet.has(answer.id);
              return (
                <ButtonBase
                  key={answer.id}
                  aria-pressed={!isResultPhase ? isSelected : undefined}
                  className={getChoiceClassName(answer)}
                  disabled={disabled || isResultPhase}
                  onClick={() => {
                    if (onSelect) {
                      onSelect(answer.id, group);
                      return;
                    }
                    onToggle?.(answer.id);
                  }}
                >
                  <Typography>{answer.label}</Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default OnGoGroupedMultiSelectChoices;
